import { Currency } from "@bentolabs/kit/entities";
import { formatCurrency } from "@bentolabs/kit/web3";
import { encodeFunctionData, parseUnits } from "viem";
import { useAccount, useChainId, useEstimateGas } from "wagmi";

import {
  createLatestPricesSelect,
  useLatestPricesQuery,
} from "shared/api/use-latest-price-query";
import { tellerABI } from "shared/config/abi";
import { tellerConfig } from "shared/config/const";
import { mul } from "shared/utils";

import { useTrade } from "../context";
import { calculateMinAmount } from "../utils";

type Props = {
  fromValue: string;
  fromCurrency: Currency | undefined;
  toCurrency: Currency;
};

export const useDepositFee = (props: Props) => {
  const { fromCurrency, fromValue, toCurrency } = props;
  const chainId = useChainId();
  const { data: latestPrices, isSuccess } = useLatestPricesQuery({
    select: createLatestPricesSelect(chainId),
  });
  const ethPrice = latestPrices?.["ETH"];
  const { address: account } = useAccount();
  const { slippage } = useTrade();

  const data = (() => {
    if (!fromValue || !fromCurrency || !account) return;
    const parsedFromValue = parseUnits(fromValue, fromCurrency.decimals);
    const minimumAmount = calculateMinAmount(fromValue, slippage);
    const parsedMinimumAmount = parseUnits(minimumAmount, toCurrency.decimals);
    return encodeFunctionData({
      abi: tellerABI,
      functionName: "deposit",
      args: [account, parsedFromValue, parsedMinimumAmount],
    });
  })();

  const gasQuery = useEstimateGas({
    data: data,
    to: tellerConfig[chainId],
    account,
    query: {
      enabled: Boolean(data && isSuccess),
    },
  });

  const feeInUsd =
    ethPrice?.price && gasQuery.data
      ? formatCurrency(mul(gasQuery.data, ethPrice.price).toString())
      : undefined;

  return { gasQuery, feeInUsd };
};
